import { ActionTree } from 'vuex';
import { TagsState, RootState } from '../types';
import { state as authState } from '../auth/auth';
import { AxiosInstance } from 'axios';
import { handleAllErrors } from '@/utils/errorHandling';
import { decodeTags } from '@/utils/decoding';

export const actions: ActionTree<TagsState, RootState> = {

	/**
	 * Fetch all tags for the current restaurant
	 *
	 * @returns {Promise<void>}
	 */
	async fetchTags({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const tags = (await axiosInst.get('/item-tags')).data;
			commit('SET_TAGS', tags);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Create a new tag
	 *
	 * @param {MenuItemTagForm} tagData - The tag data to create
	 * @returns {Promise<void>}
	 */
	async createTag({ dispatch }, tagData: MenuItemTagForm): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.post('/item-tags', { ...tagData, force_publish: true });
			await dispatch('fetchTags');
		}
		catch (error) {
			await dispatch('fetchTags');
			handleAllErrors(error);
		}
	},

	/**
	 * Update an existing tag
	 * Note: Tags are identified by name (string) - renaming a tag via PUT will update it everywhere
	 *
	 * @param {string} tagName - The current name of the tag to update
	 * @param {Partial<MenuItemTagForm>} tagData - The updated tag data
	 * @returns {Promise<void>}
	 */
	async updateTag({ dispatch }, { tagName, tagData }: { tagName: string, tagData: Partial<MenuItemTagForm> }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.put(`/item-tags/${encodeURIComponent(tagName)}`, { ...tagData, force_publish: true });
			await dispatch('fetchTags');
		}
		catch (error) {
			await dispatch('fetchTags');
			handleAllErrors(error);
		}
	},

	/**
	 * Delete a tag
	 * Note: Tags are identified by name (string)
	 *
	 * @param {string} tagName - The name of the tag to delete
	 * @returns {Promise<void>}
	 */
	async deleteTag({ dispatch }, tagName: string): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.delete(`/item-tags/${encodeURIComponent(tagName)}`, { data: { force_publish: true } });
			await dispatch('fetchTags');
		}
		catch (error) {
			await dispatch('fetchTags');
			handleAllErrors(error);
		}
	},

	// =========================================================================
	// Item Tag Associations (batch operations similar to OptionGroup associations)
	// =========================================================================

	/**
	 * Create tag associations for an item (works for both menu items and section items)
	 * Similar to createItemOptionGroupAssociation in options store
	 *
	 * @param {number} menuId - The menu ID
	 * @param {number} [sectionId] - The section ID (optional, for section items)
	 * @param {number} itemId - The item ID
	 * @param {string[]} tagNamesArray - Array of tag names to associate
	 * @returns {Promise<void>}
	 */
	async createItemTagAssociations(_context, { menuId, sectionId, itemId, tagNamesArray }: { menuId: number, sectionId?: number, itemId: number, tagNamesArray: string[] }): Promise<void> {
		if (!tagNamesArray.length) {
			return;
		}

		const axiosInst: AxiosInstance = authState.axiosInst;

		const url = sectionId
			? `/menus/${menuId}/sections/${sectionId}/items/${itemId}/tags`
			: `/menus/${menuId}/items/${itemId}/tags`;

		try {
			await axiosInst.post(url, { names: tagNamesArray });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete tag associations for an item (works for both menu items and section items)
	 * Similar to deleteItemOptionGroupAssociation in options store
	 *
	 * @param {number} menuId - The menu ID
	 * @param {number} [sectionId] - The section ID (optional, for section items)
	 * @param {number} itemId - The item ID
	 * @param {string[]} tagNamesArray - Array of tag names to disassociate
	 * @returns {Promise<void>}
	 */
	async deleteItemTagAssociations(_context, { menuId, sectionId, itemId, tagNamesArray }: { menuId: number, sectionId?: number, itemId: number, tagNamesArray: string[] }): Promise<void> {
		if (!tagNamesArray.length) {
			return;
		}

		const axiosInst: AxiosInstance = authState.axiosInst;

		const url = sectionId
			? `/menus/${menuId}/sections/${sectionId}/items/${itemId}/tags`
			: `/menus/${menuId}/items/${itemId}/tags`;

		try {
			await axiosInst.delete(url, { data: { names: tagNamesArray } });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	// =========================================================================
	// Fetch Item Tags (used by ItemEditor to load current item's tags)
	// =========================================================================

	/**
	 * Get tags for a menu item
	 *
	 * @param {number} menuId - The menu ID
	 * @param {number} itemId - The item ID
	 * @returns {Promise<MenuItemTag[]>} The item's tags, or empty array on error
	 */
	async fetchMenuItemTags(_context, { menuId, itemId }: { menuId: number, itemId: number }): Promise<MenuItemTag[]> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const tags: MenuItemTag[] = decodeTags((await axiosInst.get(`/menus/${menuId}/items/${itemId}/tags`)).data);
			return tags.sort((a, b) => +a.created_at - +b.created_at);
		}
		catch (error) {
			handleAllErrors(error);
			return [];
		}
	},

	/**
	 * Get tags for a section item
	 *
	 * @param {number} menuId - The menu ID
	 * @param {number} sectionId - The section ID
	 * @param {number} itemId - The item ID
	 * @returns {Promise<MenuItemTag[]>} The item's tags, or empty array on error
	 */
	async fetchSectionItemTags(_context, { menuId, sectionId, itemId }: { menuId: number, sectionId: number, itemId: number }): Promise<MenuItemTag[]> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const tags: MenuItemTag[] = decodeTags((await axiosInst.get(`/menus/${menuId}/sections/${sectionId}/items/${itemId}/tags`)).data);
			return tags.sort((a, b) => +a.created_at - +b.created_at);
		}
		catch (error) {
			handleAllErrors(error);
			return [];
		}
	}
};
